import { useState, useEffect, useCallback } from "react";
import { Award, Cpu, ShieldCheck, ArrowRight, Stethoscope, Gift, Play, X } from "lucide-react";
import asset from "../lib/asset";

var ADVANTAGES = [
  { icon: Award, text: "Кандидат мед. наук с опытом 28 лет" },
  { icon: Cpu, text: "Цифровое 3D-планирование операций" },
  { icon: ShieldCheck, text: "Пожизненная гарантия на импланты" },
];

var UTP_ITEMS = [
  "Бесплатная консультация",
  "Снимок КТ",
  "Удаление зубов в подарок",
];

export default function HeroSection() {
  var [videoOpen, setVideoOpen] = useState(false);

  var scrollToQuiz = function () {
    var el = document.getElementById("quiz-section");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  var closeVideo = useCallback(function () {
    setVideoOpen(false);
  }, []);

  // Пока открыт поп-ап: Escape закрывает, страница под ним не прокручивается
  useEffect(
    function () {
      if (!videoOpen) return undefined;

      var onKeyDown = function (e) {
        if (e.key === "Escape") closeVideo();
      };

      var prevOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      document.addEventListener("keydown", onKeyDown);

      return function () {
        document.body.style.overflow = prevOverflow;
        document.removeEventListener("keydown", onKeyDown);
      };
    },
    [videoOpen, closeVideo]
  );

  return (
    <section data-testid="hero-section" className="pt-20 md:pt-28 pb-12 md:pb-20 bg-white">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center">
          {/* Text */}
          <div className="animate-fade-in-up order-2 lg:order-1">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-50 border border-brand-200 text-brand-700 text-sm font-medium mb-5">
              <Stethoscope className="w-4 h-4" />
              Санкт-Петербург
            </div>

            <h1 data-testid="hero-heading" className="font-heading text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold tracking-tight leading-tight mb-3">
              Забудьте о съёмных
              <br />
              протезах <span className="text-brand-600">навсегда</span>
            </h1>

            <div className="flex flex-wrap gap-2 mb-5">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-50 border border-slate-200 text-slate-700 text-sm font-medium">
                Для всех пациентов
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-sm font-medium">
                Индивидуальные условия для пенсионеров
              </span>
            </div>

            {/* UTP */}
            <div data-testid="hero-utp" className="flex flex-col gap-2 mb-6">
              {UTP_ITEMS.map(function (item) {
                return (
                  <div key={item} className="flex items-center gap-3 px-4 py-3 rounded-xl bg-brand-50 border border-brand-100">
                    <Gift className="w-5 h-5 text-brand-600 flex-shrink-0" />
                    <span className="text-sm sm:text-base font-medium">{item}</span>
                  </div>
                );
              })}
            </div>

            {/* Advantages */}
            <div className="flex flex-col gap-3 mb-8">
              {ADVANTAGES.map(function (adv) {
                var Icon = adv.icon;
                return (
                  <div key={adv.text} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-brand-100 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-4 h-4 text-brand-600" />
                    </div>
                    <span className="text-base">{adv.text}</span>
                  </div>
                );
              })}
            </div>

            <button
              data-testid="hero-cta"
              onClick={scrollToQuiz}
              className="h-14 px-8 sm:px-10 text-base sm:text-lg font-medium rounded-full bg-brand-600 hover:bg-brand-700 text-white transition-all duration-300 shadow-lg hover:shadow-xl w-full sm:w-auto cursor-pointer inline-flex items-center justify-center gap-2"
            >
              Рассчитать стоимость
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>

          {/* Сюжет с Еленой Малышевой — статичная заставка, видео открывается в поп-апе */}
          <div className="order-1 lg:order-2 flex justify-center lg:justify-end animate-fade-in" style={{ animationDelay: "0.2s" }}>
            <div className="w-full max-w-[320px] sm:max-w-[400px] lg:max-w-[460px]">
              <button
                type="button"
                data-testid="hero-video-open"
                onClick={function () { setVideoOpen(true); }}
                className="group block w-full rounded-3xl overflow-hidden shadow-2xl border border-slate-100 bg-slate-900 cursor-pointer focus:outline-none focus:ring-4 focus:ring-brand-600/30"
              >
                <span className="relative block">
                  <img
                    src={asset("/video/poster.jpg")}
                    alt="Елена Малышева о технологии восстановления всех зубов"
                    width="455"
                    height="452"
                    loading="eager"
                    className="w-full block"
                  />

                  <span className="absolute inset-0 bg-black/10 group-hover:bg-black/25 transition-colors" />

                  <span className="absolute inset-0 flex items-center justify-center">
                    <span className="w-16 h-16 rounded-full bg-white/95 group-hover:bg-white flex items-center justify-center shadow-xl transition-transform duration-300 group-hover:scale-105">
                      <Play className="w-7 h-7 text-brand-600 fill-brand-600 ml-1" />
                    </span>
                  </span>
                </span>

                <span className="block p-4">
                  <span className="inline-flex items-center justify-center h-12 w-full px-5 rounded-full bg-brand-600 group-hover:bg-brand-700 text-white text-base font-medium shadow-lg transition-colors">
                    Смотреть видео
                  </span>
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {videoOpen && (
        <div
          data-testid="hero-video-modal"
          role="dialog"
          aria-modal="true"
          aria-label="Сюжет с Еленой Малышевой"
          onClick={closeVideo}
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 animate-fade-in"
        >
          <button
            type="button"
            data-testid="hero-video-close"
            onClick={closeVideo}
            aria-label="Закрыть видео"
            className="absolute top-4 right-4 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors cursor-pointer"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Поп-ап открывается по клику, поэтому автозапуск разрешён уже со звуком */}
          <video
            data-testid="hero-video"
            onClick={function (e) { e.stopPropagation(); }}
            src={asset("/video/malysheva.mp4")}
            poster={asset("/video/poster.jpg")}
            autoPlay
            controls
            playsInline
            preload="metadata"
            className="w-full max-w-4xl max-h-[85vh] rounded-2xl bg-black"
          >
            Ваш браузер не поддерживает воспроизведение видео.
          </video>
        </div>
      )}
    </section>
  );
}
